import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChartLine, Gift, CheckCircle, Sparkle, WarningOctagon, Users, Receipt,
  Storefront, Package, MapPin, Pulse, Gauge, EnvelopeSimple, User, Briefcase, ShareNetwork, Basket,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { AdminNavContext } from "@/pages/adminNav";
import AdminRewards from "@/pages/AdminRewards";
import AdminReports from "@/pages/AdminReports";
import AdminUsers from "@/pages/AdminUsers";
import AdminSlips from "@/pages/AdminSlips";
import AdminMerchants from "@/pages/AdminMerchants";
import AdminProducts from "@/pages/AdminProducts";
import AdminLocations from "@/pages/AdminLocations";
import AdminActivity from "@/pages/AdminActivity";
import AdminAccuracy from "@/pages/AdminAccuracy";
import AdminEmails from "@/pages/AdminEmails";
import AdminScraper from "@/pages/AdminScraper";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminAI from "@/pages/AdminAI";
import AdminHPI from "@/pages/AdminHPI";

const TABS = [
  { k: "reports", l: "Overview", icon: ChartLine },
  { k: "users", l: "Users", icon: Users },
  { k: "merchants", l: "Merchants", icon: Storefront },
  { k: "products", l: "Products", icon: Package },
  { k: "locations", l: "Locations", icon: MapPin },
  { k: "activity", l: "Activity", icon: Pulse },
  { k: "accuracy", l: "Accuracy", icon: Gauge },
  { k: "slips", l: "Slips", icon: Receipt },
  { k: "emails", l: "Emails", icon: EnvelopeSimple },
  { k: "prices", l: "Prices", icon: Storefront },
  { k: "hpi", l: "HPI Basket", icon: Basket },
  { k: "rewards", l: "Rewards", icon: Gift },
  { k: "referrals", l: "Referrals", icon: ShareNetwork },
  { k: "askai", l: "Ask AI", icon: Sparkle },
];

const VIEWS = {
  reports: AdminReports, users: AdminUsers, merchants: AdminMerchants,
  products: AdminProducts, locations: AdminLocations, activity: AdminActivity,
  accuracy: AdminAccuracy, slips: AdminSlips, emails: AdminEmails, prices: AdminScraper, hpi: AdminHPI, rewards: AdminRewards,
  referrals: AdminReferrals, askai: AdminAI,
};

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("reports");
  const [stats, setStats] = useState(null);
  const [anomCount, setAnomCount] = useState(0);
  const [slipsFilter, setSlipsFilter] = useState(null);
  const [userToOpen, setUserToOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => navigate("/"));
    api.get("/admin/anomalies").then((r) => setAnomCount(r.data?.count || 0)).catch(() => {});
  }, [navigate]);

  const openReceipts = (filter) => { setSlipsFilter(filter); setTab("slips"); };
  const openUser = (userId) => { setUserToOpen({ id: userId, ts: Date.now() }); setTab("users"); };
  const goTab = (k) => { setSlipsFilter(null); setTab(k); };
  const selectTab = (k) => { setSlipsFilter(null); setTab(k); };

  if (!stats) return <Loading />;

  const View = VIEWS[tab];

  return (
    <AdminNavContext.Provider value={{ openReceipts, openUser, goTab }}>
    <div className="space-y-6">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Admin</div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary mt-1">Admin Dashboard</h1>
      </div>

      {/* User type split */}
      <div className="grid grid-cols-3 gap-3" data-testid="admin-user-split">
        <UserStat icon={Users} label="Total users" value={stats.total_users} tone="primary" testid="stat-total-users" />
        <UserStat icon={User} label="Personal" value={stats.personal_users} sub={pct(stats.personal_users, stats.total_users)} tone="teal" testid="stat-personal-users" />
        <UserStat icon={Briefcase} label="Business" value={stats.business_users} sub={pct(stats.business_users, stats.total_users)} tone="primary" testid="stat-business-users" />
      </div>

      {/* System health strip */}
      <div className="grid grid-cols-3 gap-3">
        <Health icon={CheckCircle} label="OCR success rate" value={`${stats.ocr_success_rate}%`} tone="ok" />
        <Health icon={WarningOctagon} label="Failed receipts" value={stats.failed_receipts} tone={stats.failed_receipts > 0 ? "warn" : "ok"} />
        <Health icon={Sparkle} label="AI queries" value={stats.ai_queries} tone="ok" />
      </div>

      {/* Tabs (scrollable on mobile) */}
      <div className="flex items-center gap-1 border-b border-riq-border overflow-x-auto no-scrollbar -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => selectTab(t.k)}
            className={`relative flex items-center gap-2 whitespace-nowrap px-3.5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-riq-primary text-riq-primary" : "border-transparent text-neutral-500 hover:text-riq-primary"}`}
          >
            <t.icon size={16} weight="duotone" /> {t.l}
            {t.k === "reports" && anomCount > 0 && (
              <span data-testid="anomaly-tab-badge" className="ml-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center leading-4">{anomCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "slips" ? <AdminSlips incomingFilter={slipsFilter} /> : tab === "users" ? <AdminUsers openUserId={userToOpen} /> : <View />}
    </div>
    </AdminNavContext.Provider>
  );
}

function Health({ icon: Icon, label, value, tone }) {
  const color = tone === "warn" ? "text-riq-accent" : "text-riq-primary";
  return (
    <Card className="p-4">
      <Icon size={18} weight="duotone" className={`${color} mb-2`} />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-0.5">{value}</div>
    </Card>
  );
}

function pct(part, total) {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

function UserStat({ icon: Icon, label, value, sub, tone, testid }) {
  const teal = tone === "teal";
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`grid h-8 w-8 place-items-center rounded-xl ${teal ? "bg-teal-100 text-teal-700" : "bg-riq-primary/10 text-riq-primary"}`}>
          <Icon size={16} weight="duotone" />
        </span>
        {sub && <span className="text-xs font-semibold text-muted-foreground">{sub}</span>}
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-2.5">{label}</div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-0.5" data-testid={`${testid}-value`}>{value ?? 0}</div>
    </Card>
  );
}

